/* @HEADER
 * @version 1.1.959 | 2026-10-26
 * @purpose The real answers behind a link's questions: whether the project registry has answered (first cycle settled with a pull that succeeded), the project on screen, and whether the server lets this person see a space now through the existing relay read.
 * @sidecar navLinkPorts.ts.header.md
 * @layer app | @hex _none_ | @ctx routing
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8330, ZVX-DEV-8265, ZVX-DEV-7134

/**
 * The real answers behind the questions `judgeNavLink` asks (ZVX-4464): what the project registry has said, which project is
 * on screen, and whether the server lets this person see a space now. Kept apart from the decision so the decision stays
 * pure and these stay thin.
 *
 * «The registry has answered» is not «the first cycle has finished»: `hasFirstRegistrySyncSettled()` is also true after a
 * pull that FAILED (a failed pull must not hold the app behind a placeholder for ever, ZVX-DEV-5453). Only a pull that
 * succeeded writes `serverHeldUnknownProjects()`, so the pair is the honest reading: settled with a count is an answer,
 * settled without one is a cycle that could not ask.
 */

import { hasFirstRegistrySyncSettled, serverHeldUnknownProjects } from '../lib/projectRegistryBridge.ts';
import { getActiveProjectId } from '../lib/projectStore.ts';
import { askMayOpen } from '../features/content-access/mayOpenApi.ts';
import { readAccountIdentity } from '../features/account/accountIdentity.ts';
import { resolveActiveWorkspaceId } from '../features/admin/resolveActiveWorkspaceId.ts';
// @ts-expect-error -- framework-free .mjs, typed by its JSDoc
import { readActiveOrgId, writeActiveOrgId } from '../features/auth/activeOrgStorage.mjs';
import { listLinkProjects } from './navLinkProjects.ts';
import { createSpaceRight } from './navLinkSpaceRight.ts';
import type { NavLinkPorts, RegistryAnswer } from './navLinkResolve.ts';

export function registryAnswerFrom(settled: boolean, heldUnknownProjects: number | null): RegistryAnswer {
  if (!settled) return 'asking';
  return heldUnknownProjects === null ? 'no-answer' : 'answered';
}

export function registryAnswerNow(): RegistryAnswer {
  return registryAnswerFrom(hasFirstRegistrySyncSettled(), serverHeldUnknownProjects());
}

export interface NavLinkPortDeps {
  /** The keys under which THIS device holds the space with that server id (read from the spaces on screen). */
  localSpaceKeysFor(spaceServerId: string): readonly string[];
}

/**
 * ZVX-DEV-8353 (ZVX-4685): choose the organisation of the project a link leads into, as `WorkspaceSwitcher.handleSelect` does (the key, then the
 * `workspace-switched` event the shell listens to). Only an organisation the person belongs to, and only when it is not already the chosen one:
 * the identity is the membership, and an identity not read yet knows none. Returns whether it chose one.
 */
export function selectOrganisationOfLink(organisationId: string): boolean {
  if (typeof organisationId !== 'string' || organisationId.length === 0) return false;
  const identity = readAccountIdentity();
  if (!identity.workspaces.some((w) => w.id === organisationId)) return false;
  let stored: string | null = null;
  try {
    stored = readActiveOrgId(localStorage) as string | null;
  } catch {
    stored = null;
  }
  if (resolveActiveWorkspaceId(identity, stored) === organisationId) return false;
  try {
    if (!writeActiveOrgId(localStorage, organisationId)) return false;
    window.dispatchEvent(new CustomEvent('workspace-switched', { detail: { workspaceId: organisationId } }));
  } catch {
    return false;
  }
  return true;
}

export function createNavLinkPorts(deps: NavLinkPortDeps): NavLinkPorts {
  return {
    registry: registryAnswerNow,
    projects: listLinkProjects,
    activeProjectId: getActiveProjectId,
    // ZVX-DEV-8330: the organisation of a project is read from the identity; an identity with no workspace is one not written yet.
    identityRead: () => readAccountIdentity().workspaces.length > 0,
    selectOrganisation: selectOrganisationOfLink,
    spaceRight: createSpaceRight({
      // ZVX-DEV-8344: the server's explicit «may I open this», not the relay's body route (a space has no body).
      mayOpen: (ask) => askMayOpen(ask),
      localKeysFor: deps.localSpaceKeysFor,
    }),
  };
}
